import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UtilService } from './util.service';
import MockDetector from '../plugins/mock-detector';
import { Capacitor } from '@capacitor/core';
import { JenisAbsen, StatusAbsen } from '../models/DataPresensi';
import { Device } from '@capacitor/device';
import { Geolocation } from '@capacitor/geolocation';
import { Router } from '@angular/router';
import { PushNotifService } from './push-notif.service';
import { Notification } from '../models/Notification';
import { AuthService } from './auth.service';
import { NbToastrService } from '@nebular/theme';
import { EncryptedKeyService } from './encrypted-key.service';

@Injectable({
  providedIn: 'root',
})
export class AbsensiService {
  public isAllowedMobileSubject: BehaviorSubject<any>;
  public isAllowedLuarKantorSubject: BehaviorSubject<any>;
  public isAllowedWfhSubject: BehaviorSubject<any>;

  public isEmulatorSubject: BehaviorSubject<any>;
  public modeAbsenSubject: BehaviorSubject<number>;
  public jenisAbsenSubject: BehaviorSubject<JenisAbsen>;
  public statusAbsenSubject: BehaviorSubject<StatusAbsen>;
  public deviceIdSubject: BehaviorSubject<any>;
  public isAllowAbsenWajah = new BehaviorSubject<boolean>(false);

  // Bukti luar kantor state
  public buktiBase64Subject = new BehaviorSubject<string | null>(null);
  public lokasiAbsenSubject = new BehaviorSubject<string | null>(null);
  public keteranganSubject = new BehaviorSubject<string | null>(null);

  private readonly CACHE_DURATION = 10 * 60 * 60 * 1000; // 10 jam dalam milidetik

  constructor(
    private utilService: UtilService,
    private router: Router,
    private pushNotif: PushNotifService,
    private authService: AuthService,
    private toastrService: NbToastrService,
    private encryptedKeyService: EncryptedKeyService,
  ) {
    this.isAllowedMobileSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem('allowMobile')),
    );
    this.isAllowedLuarKantorSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem('allowLuarKantor')),
    );
    this.isAllowedWfhSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem('allowWfh')),
    );

    // Baru jalankan pengecekan (akan menggunakan cache jika masih valid)
    this.checkAllowMobile();
    this.checkAllowAbsenLuar();
    this.checkAllowWfh();

    if (Capacitor.isNativePlatform()) {
      this.isEmulatorSubject = new BehaviorSubject<any>(
        JSON.parse(localStorage.getItem('isEmu')),
      );
      this.checkEmulator();
    }

    this.modeAbsenSubject = new BehaviorSubject<number>(null);
    this.jenisAbsenSubject = new BehaviorSubject<JenisAbsen>(
      JSON.parse(localStorage.getItem('jenis_absen')),
    );
    this.deviceIdSubject = new BehaviorSubject<any>(
      localStorage.getItem('deviceid'),
    );
    this.statusAbsenSubject = new BehaviorSubject<StatusAbsen>(
      JSON.parse(localStorage.getItem('status_absen')),
    );

    this.isAllowWajah();

    this.authService.currentUser.subscribe((user) => {
      if (user && user.id) {
        const lastUserId = localStorage.getItem('lastUserId');
        if (lastUserId && lastUserId !== user.id.toString()) {
          this.clearAbsensiData();
          this.checkAllowMobile();
          this.checkAllowAbsenLuar();
          this.checkAllowWfh();
          this.isAllowWajah();
        }
        localStorage.setItem('lastUserId', user.id.toString());
      }
    });
  }

  clearAbsensiData() {
    const keysToRemove = [
      'allowMobile',
      'allowMobile_TIME',
      'allowLuarKantor',
      'allowLuarKantor_TIME',
      'allowWfh',
      'allowWfh_TIME',
      'jenis_absen',
      'status_absen',
    ];

    keysToRemove.forEach((key) => localStorage.removeItem(key));

    this.isAllowedMobileSubject?.next(null);
    this.isAllowedLuarKantorSubject?.next(null);
    this.isAllowedWfhSubject?.next(null);
    this.modeAbsenSubject?.next(null);
    this.jenisAbsenSubject?.next(null);
    this.statusAbsenSubject?.next(null);
    this.clearBuktiData();
  }

  private fetchWithCache(
    cacheKey: string,
    endpoint: string,
    subject: BehaviorSubject<any>,
  ) {
    const timeKey = `${cacheKey}_TIME`;
    const now = new Date().getTime();
    const cachedTime = localStorage.getItem(timeKey);

    if (cachedTime && now - parseInt(cachedTime, 10) < this.CACHE_DURATION) {
      return;
    }

    this.utilService.apiGet(endpoint, '').subscribe({
      next: (res) => {
        localStorage.setItem(cacheKey, JSON.stringify(res));
        localStorage.setItem(timeKey, now.toString());
        subject.next(res);
      },
      error: (err) => {
        this.toastrService.danger(`Gagal memuat status ${cacheKey}`, 'Error');
        console.error(`Error fetching ${endpoint}`, err);
      },
    });
  }

  checkAllowMobile() {
    this.fetchWithCache(
      'allowMobile',
      'absensi/allow-mobile',
      this.isAllowedMobileSubject,
    );
  }

  checkAllowAbsenLuar() {
    this.fetchWithCache(
      'allowLuarKantor',
      'absensi/allow-luarkantor',
      this.isAllowedLuarKantorSubject,
    );
  }

  checkAllowWfh() {
    this.fetchWithCache(
      'allowWfh',
      'absensi/allow-wfh',
      this.isAllowedWfhSubject,
    );
  }

  async checkEmulator() {
    let deviceInfo = (await MockDetector.isMock()).deviceInfo;
    let body = {
      json: JSON.stringify(deviceInfo),
    };
    this.utilService.apiPost('sys/isemulator', body).subscribe((val) => {
      this.isEmulatorSubject.next(val);
      localStorage.setItem('isEmu', JSON.stringify(val));
    });
  }

  public get isAllowMobile() {
    return this.isAllowedMobileSubject.value;
  }

  public get isAllowLuarKantor() {
    return this.isAllowedLuarKantorSubject.value;
  }

  public get isAllowWfh() {
    return this.isAllowedWfhSubject.value;
  }

  public get isEmulatorValue() {
    return this.isEmulatorSubject.value;
  }

  storeDeviceId(data) {
    localStorage.setItem('deviceid', JSON.stringify(data));
    this.deviceIdSubject.next(JSON.stringify(data));
  }

  storeModeAbsen(mode: number) {
    this.modeAbsenSubject.next(mode);
  }

  storeJensiAbsen(data) {
    localStorage.setItem('jenis_absen', JSON.stringify(data));
    this.jenisAbsenSubject.next(data);
  }

  storeStatusAbsen(data: StatusAbsen) {
    localStorage.setItem('status_absen', JSON.stringify(data));
    this.statusAbsenSubject.next(data);
  }

  storeBuktiData(
    bukti: string | null,
    lokasi: string | null,
    keterangan: string | null,
  ) {
    this.buktiBase64Subject.next(bukti);
    this.lokasiAbsenSubject.next(lokasi);
    this.keteranganSubject.next(keterangan);
  }

  clearBuktiData() {
    this.buktiBase64Subject.next(null);
    this.lokasiAbsenSubject.next(null);
    this.keteranganSubject.next(null);
  }

  public get deviceId() {
    return this.deviceIdSubject.value;
  }

  public get modeAbsen(): number {
    return this.modeAbsenSubject.value;
  }

  public get jenisAbsen(): JenisAbsen {
    return this.jenisAbsenSubject.value;
  }

  public get statusAbsen(): StatusAbsen {
    return this.statusAbsenSubject.value;
  }

  public get buktiBase64(): string | null {
    return this.buktiBase64Subject.value;
  }

  public get lokasiAbsen(): string | null {
    return this.lokasiAbsenSubject.value;
  }

  public get keterangan(): string | null {
    return this.keteranganSubject.value;
  }

  async proceed(jenis_absen: JenisAbsen) {
    try {
      const [deviceInfo, position] = await Promise.all([
        this.getDeviceInfo(),
        this.getLocationInfo(),
      ]);

      const body = await this.createRequestBody(
        jenis_absen,
        deviceInfo,
        position,
      );

      await this.submitAttendance(jenis_absen, body);
    } catch (e) {
      this.handleError('Gagal proses absen : ' + e);
    }
  }

  private async getDeviceInfo() {
    const device = {
      id: (await Device.getId()).identifier,
      model: (await Device.getInfo()).model,
      name: (await Device.getInfo()).name,
      manufacturer: (await Device.getInfo()).manufacturer,
      sdk: (await Device.getInfo()).androidSDKVersion,
    };
    return device;
  }

  private async getLocationInfo() {
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 30000,
    });

    return {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };
  }

  private async createRequestBody(
    jenis_absen: JenisAbsen,
    deviceInfo: any,
    position: any,
  ) {
    const user = this.authService.currentUserValue;
    let pegawai_id = user.pegawai_id;

    const dataToEncrypt = `${pegawai_id}|${position.lat}|${position.lng}|${deviceInfo.model}`;

    const encryptedPayload =
      await this.encryptedKeyService.encrypt(dataToEncrypt);

    const body: any = {
      lat: position.lat,
      lon: position.lng,
      deviceid: this.deviceId || null,
      deviceid_in: null,
      devicemodel_in: null,
      devicename_in: null,
      devicemanufactur_in: null,
      devicesdk_in: null,
      absen_in_photo: null,
      deviceid_out: null,
      devicemodel_out: null,
      devicename_out: null,
      devicemanufactur_out: null,
      devicesdk_out: null,
      absen_out_photo: null,
      secure_payload: encryptedPayload,
    };

    const suffix = jenis_absen?.stat === 'masuk' ? 'in' : 'out';
    body[`deviceid_${suffix}`] = deviceInfo.id;
    body[`devicemodel_${suffix}`] = deviceInfo.model;
    body[`devicename_${suffix}`] = deviceInfo.name;
    body[`devicemanufactur_${suffix}`] = deviceInfo.manufacturer;
    body[`devicesdk_${suffix}`] = deviceInfo.sdk;
    body[`absen_${suffix}_photo`] = null;

    if (
      jenis_absen?.jenis === 'absen_luar' ||
      jenis_absen?.jenis === 'absen_wfh'
    ) {
      body.record_type = 4;
      body.mobile_type = jenis_absen?.mobile_type || null;

      const rawPhoto = this.buktiBase64;
      const formattedBase64 = rawPhoto
        ? `data:image/jpeg;base64,${rawPhoto}`
        : null;

      body[`absen_${suffix}_photo`] = formattedBase64;
      body.keterangan = this.keterangan || null;

      if (jenis_absen?.jenis === 'absen_luar') {
        body.lokasi_absen = this.lokasiAbsen || null;
      }
    }

    return body;
  }

  private async submitAttendance(jenis_absen: JenisAbsen, body: any) {
    const { jenis, stat, isUlang } = jenis_absen;

    const typeMap: { [key: string]: string } = {
      absen_biasa: '',
      absen_luar: '-luarkantor',
      absen_wfh: '-wfh',
    };

    const prefix = typeMap[jenis] || '';

    const endpoint = `${stat}${prefix}${isUlang ? '-ulang' : ''}`;

    await this.callAttendanceApi(endpoint, body);
  }

  private callAttendanceApi(endpoint: string, body: any) {
    return new Promise<void>((resolve, reject) => {
      this.utilService.apiPost(`v3/absensi/${endpoint}`, body).subscribe({
        next: (res) => {
          if (res && res['success']) {
            if (res['data']?.['deviceid']) {
              this.storeDeviceId(res['data']['deviceid']);
            }

            if (endpoint.includes('masuk') && res['notifs']) {
              this.handleNotifications(res['notifs']);
            }

            this.clearBuktiData();
            this.storeStatusAbsen({
              success: true,
              title: 'Absen Berhasil',
              text: res['msg'],
            });
            this.router.navigate(['/mobile-absensi/status']);
          } else {
            this.handleError(res['msg']);
          }
          resolve();
        },
        error: (err) => {
          this.handleError(err);
          reject();
        },
      });
    });
  }

  private handleNotifications(notifs: Notification[]) {
    notifs.forEach((el) => {
      this.pushNotif.setScheduledNotif(el.title, el.body, el.at);
    });
  }

  private handleError(message: string) {
    this.storeStatusAbsen({
      success: false,
      title: 'Absen Gagal',
      text: message,
    });
    this.router.navigate(['/mobile-absensi/status']);
  }

  isAllowWajah() {
    this.authService.getUser().subscribe({
      next: (res) => {
        this.isAllowAbsenWajah.next(res['allow_face']);
      },
    });
  }

  forceRefreshAllowedMethod() {
    localStorage.removeItem('allowMobile_TIME');
    localStorage.removeItem('allowLuarKantor_TIME');
    localStorage.removeItem('allowWfh_TIME');

    localStorage.removeItem('PRESENCE_LOCATIONS_TIME');

    this.checkAllowMobile();
    this.checkAllowAbsenLuar();
    this.checkAllowWfh();
  }
}
